import { CellValue } from 'jspreadsheet-ce';
import { Channel } from '../models/channel';
import {
    ChannelType,
    ChannelTypeStrings,
    isAnalog,
    isBlank,
    isDigital,
    isDigitalMotor,
    isExhGasDev,
    isExhGasMean,
    isFlowCounter,
    isRunningHour,
    isSystemChannel,
    SimpleTypes,
    SpecialSimpleType,
} from '../models/channel-type';
import { MotorType } from '../models/motor-alarm-setting';
import { ChannelFcuData } from '../models/channel-fcu-data';
import { AlarmLevel } from '../models/alarm-level';
import { systemChannelType } from '../models/system-channels';

export function getChannelStatusString(channel: Channel): string {
    if (isBlank(channel.opsData.channelType)) return '';
    const channelType = channel.fcuData.channelType;

    if (isDigitalMotor(channelType)) return channel.fcuData.motorChannelSetting.motorType;
    else if (isDigital(channelType)) {
        const contactOn = channel.fcuData.digitalAlarmSetting.statusStringContactOn;
        const contactOff = channel.fcuData.digitalAlarmSetting.statusStringContactOff;
        return `${contactOn}/${contactOff}`;
    }

    const hasAnalogAlarmStructure =
        isAnalog(channelType) ||
        channelType === ChannelType.FLOW_TOTAL ||
        channelType === ChannelType.FLOW_DAY ||
        channelType === ChannelType.FLOW_EXT_COMM ||
        channelType === ChannelType.FLOW_LOGIC ||
        channelType === ChannelType.RUNNING_TOTAL ||
        channelType === ChannelType.RUNNING_DAY ||
        channelType === ChannelType.RUNNING_LOGIC ||
        channelType === ChannelType.EXH_GAS_DEV ||
        channelType === ChannelType.EXH_GAS_MEAN;

    if (hasAnalogAlarmStructure) {
        const enabledSettings = [];
        if (channel.fcuData.alarmSettingVeryHigh.enable) {
            enabledSettings.push(channel.fcuData.alarmSettingVeryHigh.statusString || 'HH');
        }
        if (channel.fcuData.alarmSettingHigh.enable) {
            enabledSettings.push(channel.fcuData.alarmSettingHigh.statusString || 'HIGH');
        }

        enabledSettings.push('NOR');

        if (channel.fcuData.alarmSettingLow.enable) {
            enabledSettings.push(channel.fcuData.alarmSettingLow.statusString || 'LOW');
        }
        if (channel.fcuData.alarmSettingVeryLow.enable) {
            enabledSettings.push(channel.fcuData.alarmSettingVeryLow.statusString || 'LL');
        }
        return enabledSettings.join('/');
    }

    if (isSystemChannel(channelType)) {
        const systemSettings = channel.fcuData.systemAlarmSetting;
        const status = [];
        if (!systemSettings) return;
        for (let i = 1; i <= 16; i++) {
            const statusSetting = `statusString${i}`;
            const statusString = systemSettings[statusSetting];
            if (statusString) status.push(statusString);
        }
        return status.join('/');
    }

    return '';
}

export function setChannelStatusFromString(fcuData: ChannelFcuData, status: string): ChannelFcuData {
    if (fcuData.channelType == ChannelType.BLANK) return fcuData;
    const channelType = fcuData.channelType;

    if (isDigitalMotor(channelType)) {
        const motorChannelType = Object.values(MotorType).find(type => type.toLowerCase() == status.toLowerCase());
        fcuData.motorChannelSetting.motorType = motorChannelType ?? MotorType.UNDEFINED;
    } else if (isDigital(channelType)) {
        const parts = status.split('/');
        fcuData.digitalAlarmSetting.statusStringContactOn = parts[0] ?? '';
        fcuData.digitalAlarmSetting.statusStringContactOff = parts[1] ?? '';
    } else if (isSystemChannel(channelType)) {
        const parts = status.split('/');
        if (parts.length > 0) fcuData.systemAlarmSetting.enable = true;

        for (let i = 1; i <= 16; i++) {
            (fcuData.systemAlarmSetting as any)[`statusString${i}`] = parts[i - 1] ?? '';
        }
    } else {
        const hasAnalogAlarmStructure =
            isAnalog(channelType) ||
            isFlowCounter(channelType) ||
            isRunningHour(channelType) ||
            isExhGasDev(channelType) ||
            isExhGasMean(channelType);

        if (hasAnalogAlarmStructure) {
            const withDefault = <T extends number>(value: T, fallback: T): T => (isFinite(value) ? value : fallback);

            const configureAlarms = (aboveParts: string[], belowParts: string[]) => {
                const setup = (obj: any, status: string, enable: boolean) => {
                    obj.enable = enable;
                    obj.statusString = enable ? status : '';

                    if (enable) {
                        obj.alarmLevel = withDefault(obj.alarmLevel, AlarmLevel.Alarm);
                        obj.alarmGroupNo = withDefault(obj.alarmGroupNo, 1);
                        obj.reposeGroup1No = withDefault(obj.reposeGroup1No, 0);
                        obj.reposeGroup2No = withDefault(obj.reposeGroup2No, 0);
                        obj.delayTimer = withDefault(obj.delayTimer, 0);
                    }
                };

                setup(
                    fcuData.alarmSettingHigh,
                    aboveParts.length > 0 ? aboveParts[aboveParts.length - 1] : '',
                    aboveParts.length > 0
                );

                setup(fcuData.alarmSettingVeryHigh, aboveParts.length > 1 ? aboveParts[0] : '', aboveParts.length > 1);

                setup(fcuData.alarmSettingLow, belowParts.length > 0 ? belowParts[0] : '', belowParts.length > 0);

                setup(fcuData.alarmSettingVeryLow, belowParts.length > 1 ? belowParts[1] : '', belowParts.length > 1);
            };

            const parts = status.split('/');
            const norIndex = parts.indexOf('NOR');
            const aboveParts = norIndex > 0 ? parts.slice(0, norIndex) : [];
            const belowParts = norIndex >= 0 ? parts.slice(norIndex + 1) : [];

            configureAlarms(aboveParts, belowParts);
        }
    }
    return fcuData;
}

export function getSimpleChannelTypes(currentTypes: CellValue[], currentNormalOpen: CellValue[]) {
    if (!currentTypes || currentTypes.length == 0) return [];

    const updatedData = currentTypes.map((channelType, index) => {
        if (channelType == 'Digital (contact)') {
            const isNormalOpen = currentNormalOpen[index];
            if (isNormalOpen) return SimpleTypes.NO;
            else return SimpleTypes.NC;
        } else if (channelType == 'Digital (Motor)') return SimpleTypes.M1;
        else if (channelType == 'Analog (4-20mA PT)') return SimpleTypes.PT;
        else if (channelType == 'Analog (4-20mA AI)' || channelType == 'Analog (1-5V)') {
            return SimpleTypes.AI;
        } else if (channelType == 'Analog (Temp Pt100 3-wire)' || channelType == 'Analog (Temp Pt100 2-wire)') {
            return SimpleTypes.TR;
        } else if (channelType == 'Analog (Temp K)') return SimpleTypes.K;
        else if (channelType == 'Exh gas dev') return SimpleTypes.DV;
        else if (channelType == 'Exh gas mean') return SimpleTypes.MT;
        else if (channelType == 'Flow counter (Total)') return SimpleTypes.PU;
        else if (channelType == 'Flow counter (Day)') return SimpleTypes.PUD;
        else if (channelType == 'Running hour (Total)') return SimpleTypes.R1;
        else if (channelType == 'Running hour (Day)') return SimpleTypes.R1D;
        else if (channelType == systemChannelType) return SimpleTypes.SYS;
        else if (channelType == 'Digital (Ext Comm.)') return SimpleTypes.EXT_DI;
        else if (channelType == 'Digital (Logic)') return SimpleTypes.L_DI;
        else if (channelType == 'Analog (Ext Comm.)') return SimpleTypes.EXT_AI;
        else if (channelType == 'Analog (Logic)') return SimpleTypes.L_AI;
        else if (channelType == 'Flow counter (Ext Comm.)') return SimpleTypes.EXT_PU;
        else if (channelType == 'Flow counter (Logic)') return SimpleTypes.L_PU;
        else if (channelType == 'Running hour (Logic)') return SimpleTypes.L_R1;
        return channelType;
    });
    return updatedData;
}

export function getSimpleTypesFromString(channelType: string, isNormalOpen: boolean) {
    if (!channelType) return '';

    if (channelType == 'Digital (contact)') {
        if (isNormalOpen) return SimpleTypes.NO;
        else return SimpleTypes.NC;
    } else if (channelType == 'Digital (Motor)') return SimpleTypes.M1;
    else if (channelType == 'Analog (4-20mA PT)') return SimpleTypes.PT;
    else if (channelType == 'Analog (4-20mA AI)' || channelType == 'Analog (1-5V)') {
        return SimpleTypes.AI;
    } else if (channelType == 'Analog (Temp Pt100 3-wire)' || channelType == 'Analog (Temp Pt100 2-wire)') {
        return SimpleTypes.TR;
    } else if (channelType == 'Analog (Temp K)') return SimpleTypes.K;
    else if (channelType == 'Exh gas dev') return SimpleTypes.DV;
    else if (channelType == 'Exh gas mean') return SimpleTypes.MT;
    else if (channelType == 'Flow counter (Total)') return SimpleTypes.PU;
    else if (channelType == 'Flow counter (Day)') return SimpleTypes.PUD;
    else if (channelType == 'Running hour (Total)') return SimpleTypes.R1;
    else if (channelType == 'Running hour (Day)') return SimpleTypes.R1D;
    else if (channelType == systemChannelType) return SimpleTypes.SYS;
    else if (channelType == 'Digital (Ext Comm.)') return SimpleTypes.EXT_DI;
    else if (channelType == 'Digital (Logic)') return SimpleTypes.L_DI;
    else if (channelType == 'Analog (Ext Comm.)') return SimpleTypes.EXT_AI;
    else if (channelType == 'Analog (Logic)') return SimpleTypes.L_AI;
    else if (channelType == 'Flow counter (Ext Comm.)') return SimpleTypes.EXT_PU;
    else if (channelType == 'Flow counter (Logic)') return SimpleTypes.L_PU;
    else if (channelType == 'Running hour (Logic)') return SimpleTypes.L_R1;
    return '';
}

export function getChannelTypeFromSimpleType(currentType: SimpleTypes, specialType: SpecialSimpleType): ChannelType {
    if (!currentType) return ChannelType.BLANK;
    if (currentType == SimpleTypes.NO) {
        if (specialType == SpecialSimpleType.R) return ChannelType.DIGITAL_EXT_COMM;
        else if (specialType == SpecialSimpleType.W) return ChannelType.DIGITAL_LOGIC;
        else return ChannelType.DIGITAL_CONTACT;
    } else if (currentType == SimpleTypes.NC) return ChannelType.DIGITAL_CONTACT;
    else if (currentType == SimpleTypes.M1) return ChannelType.DIGITAL_MOTOR;
    else if (currentType == SimpleTypes.PT) return ChannelType.ANALOG_420MA_PT;
    else if (currentType == SimpleTypes.AI) {
        if (specialType == SpecialSimpleType.R) return ChannelType.ANALOG_EXT_COMM;
        else if (specialType == SpecialSimpleType.W) return ChannelType.ANALOG_LOGIC;
        else return ChannelType.ANALOG_420MA_AI;
    } else if (currentType == SimpleTypes.TR) return ChannelType.ANALOG_PT100_3WIRE;
    else if (currentType == SimpleTypes.K) return ChannelType.ANALOG_TEMP_K;
    else if (currentType == SimpleTypes.DV) return ChannelType.EXH_GAS_DEV;
    else if (currentType == SimpleTypes.MT) return ChannelType.EXH_GAS_MEAN;
    else if (currentType == SimpleTypes.PU) {
        if (specialType == SpecialSimpleType.R) return ChannelType.FLOW_EXT_COMM;
        else if (specialType == SpecialSimpleType.W) return ChannelType.FLOW_LOGIC;
        return ChannelType.FLOW_TOTAL;
    } else if (currentType == SimpleTypes.PUD) return ChannelType.FLOW_DAY;
    else if (currentType == SimpleTypes.R1) {
        if (specialType == SpecialSimpleType.W) return ChannelType.RUNNING_LOGIC;
        return ChannelType.RUNNING_TOTAL;
    } else if (currentType == SimpleTypes.R1D) return ChannelType.RUNNING_DAY;
    else if (currentType == SimpleTypes.SYS) return ChannelType.SYSTEM;
    return ChannelType.BLANK;
}

export function getSpecialType(channelType: ChannelType): SpecialSimpleType {
    if (!channelType) return;
    if (
        channelType == ChannelType.DIGITAL_EXT_COMM ||
        channelType == ChannelType.ANALOG_EXT_COMM ||
        channelType == ChannelType.FLOW_EXT_COMM
    ) {
        return SpecialSimpleType.R;
    } else if (
        channelType == ChannelType.DIGITAL_LOGIC ||
        channelType == ChannelType.ANALOG_LOGIC ||
        channelType == ChannelType.FLOW_LOGIC ||
        channelType == ChannelType.RUNNING_LOGIC
    ) {
        return SpecialSimpleType.W;
    } else if (channelType == ChannelType.SYSTEM) return SpecialSimpleType.S;
    return;
}

export function getChannelStringFromSimpleType(currentType: SimpleTypes): string {
    if (!currentType) return ChannelTypeStrings[ChannelType.BLANK];
    if (currentType == SimpleTypes.NO || currentType == SimpleTypes.NC)
        return ChannelTypeStrings[ChannelType.DIGITAL_CONTACT];
    else if (currentType == SimpleTypes.M1) return ChannelTypeStrings[ChannelType.DIGITAL_MOTOR];
    else if (currentType == SimpleTypes.PT) return ChannelTypeStrings[ChannelType.ANALOG_420MA_PT];
    else if (currentType == SimpleTypes.AI) return ChannelTypeStrings[ChannelType.ANALOG_420MA_AI];
    else if (currentType == SimpleTypes.TR) return ChannelTypeStrings[ChannelType.ANALOG_PT100_3WIRE];
    else if (currentType == SimpleTypes.K) return ChannelTypeStrings[ChannelType.ANALOG_TEMP_K];
    else if (currentType == SimpleTypes.DV) return ChannelTypeStrings[ChannelType.EXH_GAS_DEV];
    else if (currentType == SimpleTypes.MT) return ChannelTypeStrings[ChannelType.EXH_GAS_MEAN];
    else if (currentType == SimpleTypes.PU) return ChannelTypeStrings[ChannelType.FLOW_TOTAL];
    else if (currentType == SimpleTypes.PUD) return ChannelTypeStrings[ChannelType.FLOW_DAY];
    else if (currentType == SimpleTypes.R1) return ChannelTypeStrings[ChannelType.RUNNING_TOTAL];
    else if (currentType == SimpleTypes.R1D) return ChannelTypeStrings[ChannelType.RUNNING_DAY];
    else if (currentType == SimpleTypes.SYS) return ChannelTypeStrings[ChannelType.SYSTEM];
    else if (currentType == SimpleTypes.EXT_AI) return ChannelTypeStrings[ChannelType.ANALOG_EXT_COMM];
    else if (currentType == SimpleTypes.L_AI) return ChannelTypeStrings[ChannelType.ANALOG_LOGIC];
    else if (currentType == SimpleTypes.EXT_DI) return ChannelTypeStrings[ChannelType.DIGITAL_EXT_COMM];
    else if (currentType == SimpleTypes.L_DI) return ChannelTypeStrings[ChannelType.DIGITAL_LOGIC];
    else if (currentType == SimpleTypes.EXT_PU) return ChannelTypeStrings[ChannelType.FLOW_EXT_COMM];
    else if (currentType == SimpleTypes.L_PU) return ChannelTypeStrings[ChannelType.FLOW_LOGIC];
    else if (currentType == SimpleTypes.L_R1) return ChannelTypeStrings[ChannelType.RUNNING_LOGIC];

    return ChannelTypeStrings[ChannelType.BLANK];
}

export function getSimpleType(fcuData: ChannelFcuData) {
    if (!fcuData.channelType) return '';
    if (fcuData.channelType === ChannelType.DIGITAL_CONTACT) {
        if (fcuData.digitalAlarmSetting.normalOpenFlag) return SimpleTypes.NO;
        else return SimpleTypes.NC;
    } else if (fcuData.channelType === ChannelType.DIGITAL_MOTOR) return SimpleTypes.M1;
    else if (fcuData.channelType === ChannelType.ANALOG_420MA_PT) return SimpleTypes.PT;
    else if (fcuData.channelType === ChannelType.ANALOG_1_5_V || fcuData.channelType === ChannelType.ANALOG_420MA_AI) {
        return SimpleTypes.AI;
    } else if (
        fcuData.channelType === ChannelType.ANALOG_PT100_3WIRE ||
        fcuData.channelType === ChannelType.ANALOG_PT100_2WIRE
    ) {
        return SimpleTypes.TR;
    } else if (fcuData.channelType === ChannelType.ANALOG_TEMP_K) return SimpleTypes.K;
    else if (fcuData.channelType === ChannelType.EXH_GAS_DEV) return SimpleTypes.DV;
    else if (fcuData.channelType === ChannelType.EXH_GAS_MEAN) return SimpleTypes.MT;
    else if (fcuData.channelType === ChannelType.FLOW_TOTAL) return SimpleTypes.PU;
    else if (fcuData.channelType === ChannelType.FLOW_DAY) return SimpleTypes.PUD;
    else if (fcuData.channelType === ChannelType.RUNNING_TOTAL) return SimpleTypes.R1;
    else if (fcuData.channelType === ChannelType.RUNNING_DAY) return SimpleTypes.R1D;
    else if (fcuData.channelType === ChannelType.DIGITAL_EXT_COMM) return SimpleTypes.NO;
    else if (fcuData.channelType === ChannelType.DIGITAL_LOGIC) return SimpleTypes.NO;
    else if (fcuData.channelType === ChannelType.ANALOG_EXT_COMM) return SimpleTypes.AI;
    else if (fcuData.channelType === ChannelType.ANALOG_LOGIC) return SimpleTypes.AI;
    else if (fcuData.channelType === ChannelType.FLOW_EXT_COMM) return SimpleTypes.PU;
    else if (fcuData.channelType === ChannelType.FLOW_LOGIC) return SimpleTypes.PU;
    else if (fcuData.channelType === ChannelType.RUNNING_LOGIC) return SimpleTypes.R1;
    else if (fcuData.channelType === ChannelType.SYSTEM) return SimpleTypes.SYS;

    return '';
}

export function getSimpleTypeChannelSheet(fcuData: ChannelFcuData) {
    if (!fcuData.channelType) return '';
    if (fcuData.channelType === ChannelType.DIGITAL_CONTACT) {
        if (fcuData.digitalAlarmSetting.normalOpenFlag) return SimpleTypes.NO;
        else return SimpleTypes.NC;
    } else if (fcuData.channelType === ChannelType.DIGITAL_MOTOR) return SimpleTypes.M1;
    else if (fcuData.channelType === ChannelType.ANALOG_420MA_PT) return SimpleTypes.PT;
    else if (fcuData.channelType === ChannelType.ANALOG_1_5_V || fcuData.channelType === ChannelType.ANALOG_420MA_AI) {
        return SimpleTypes.AI;
    } else if (
        fcuData.channelType === ChannelType.ANALOG_PT100_3WIRE ||
        fcuData.channelType === ChannelType.ANALOG_PT100_2WIRE
    ) {
        return SimpleTypes.TR;
    } else if (fcuData.channelType === ChannelType.ANALOG_TEMP_K) return SimpleTypes.K;
    else if (fcuData.channelType === ChannelType.EXH_GAS_DEV) return SimpleTypes.DV;
    else if (fcuData.channelType === ChannelType.EXH_GAS_MEAN) return SimpleTypes.MT;
    else if (fcuData.channelType === ChannelType.FLOW_TOTAL) return SimpleTypes.PU;
    else if (fcuData.channelType === ChannelType.FLOW_DAY) return SimpleTypes.PUD;
    else if (fcuData.channelType === ChannelType.RUNNING_TOTAL) return SimpleTypes.R1;
    else if (fcuData.channelType === ChannelType.RUNNING_DAY) return SimpleTypes.R1D;
    else if (fcuData.channelType === ChannelType.SYSTEM) return SimpleTypes.SYS;
    else if (fcuData.channelType === ChannelType.DIGITAL_EXT_COMM) return SimpleTypes.EXT_DI;
    else if (fcuData.channelType === ChannelType.DIGITAL_LOGIC) return SimpleTypes.L_DI;
    else if (fcuData.channelType === ChannelType.ANALOG_EXT_COMM) return SimpleTypes.EXT_AI;
    else if (fcuData.channelType === ChannelType.ANALOG_LOGIC) return SimpleTypes.L_AI;
    else if (fcuData.channelType === ChannelType.FLOW_EXT_COMM) return SimpleTypes.EXT_PU;
    else if (fcuData.channelType === ChannelType.FLOW_LOGIC) return SimpleTypes.L_PU;
    else if (fcuData.channelType === ChannelType.RUNNING_LOGIC) return SimpleTypes.L_R1;

    return '';
}

export class SystemAlarmSetting {
    enable: boolean;
    statusStrings: string[] = Array(16).fill('');
}
